<template>
  <TText size="lg" tag="h1" variant="semibold" classes="mb-2">{{
    t('auth.login.title')
  }}</TText>

  <ApiResponseError
    v-if="currentState === 'ERROR'"
    :api-response-error="apiResponseError"
    data-test="apiResponseError"
    class="my-4"
  />

  <div class="flex flex-col justify-center">
    <UButton
      color="neutral"
      variant="subtle"
      icon="i-logos-google-icon"
      size="lg"
      block
      class="my-3"
      data-test="googleButton"
      :disabled="!isGoogleReady"
      @click="loginToGoogle()"
    >
      {{ t('auth.login.google.button') }}
    </UButton>
  </div>

  <TText classes="mb-5 mt-2 text-sm text-center">
    {{ t('auth.login.separator') }}
  </TText>

  <UForm :schema="schema" :state="state" class="space-y-3" @submit="emailLogin">
    <UFormField name="emailAddress">
      <UInput
        v-model="state.emailAddress"
        size="lg"
        :placeholder="t('auth.login.email.email')"
        :aria-label="t('auth.login.email.email')"
        :title="t('auth.login.email.mail-title')"
        autocomplete="on"
      />
    </UFormField>

    <UFormField name="password">
      <TPassword
        v-model="state.password"
        :placeholder="t('auth.login.email.password')"
        :title="t('auth.login.email.password-title')"
        autocomplete="on"
      />
    </UFormField>

    <div>
      <ULink
        class="text-sm"
        data-test="rememberPasswordLink"
        :aria-label="t('auth.login.email.password-forgot-label')"
        rel="nofollow"
        :href="routes().rememberPassword()"
        external
      >
        {{ t('auth.login.email.password-forgot') }}
      </ULink>
    </div>

    <div class="flex flex-col items-center">
      <UButton
        color="neutral"
        class="mt-2 mb-8"
        variant="solid"
        size="xl"
        type="submit"
        block
        :disabled="!isValid"
      >
        {{ t('auth.login.email.submit') }}
      </UButton>
    </div>
  </UForm>
  <TOverlay
    class="z-20"
    :fixed="false"
    :show="currentState === STATES.LOADING"
  />
</template>

<script setup lang="ts">
import { browserFetch as fetchLoginEmail } from '~~/modules/api/rurality/v3/users/login'
import { paramsSchema } from '~~/shared/api/rurality/v3/users/login/paramsSchema'
import { scrollToTop } from '~/composables/useAnimations'
import { useZodValidator } from '~~/modules/form/composables/useZodValidator'
import { useGoogleSignIn } from '~~/modules/auth/composables/useGoogleSignIn'
import loggedUserRedirect from '~~/modules/auth/composables/useAuthRedirect'

const props = defineProps<{
  isModal?: boolean
}>()

const emit = defineEmits(['login-success'])

const apiResponseError: Ref<ApiResponseError | null> = ref(null)

const { validateEmail, validatePassword } = useZodValidator()

const schema = paramsSchema.extend({
  emailAddress: validateEmail(),
  password: validatePassword(),
})

const state = reactive({
  emailAddress: '',
  password: '',
})

const STATES = {
  IDLE: 'IDLE',
  LOADING: 'LOADING',
  ERROR: 'ERROR',
}

const currentState = ref(STATES.IDLE)

const isValid = computed(() => schema.safeParse(state).success)

async function emailLogin(event: any) {
  const result = schema.safeParse(event.data)
  if (result.success) {
    currentState.value = STATES.LOADING

    const login = await fetchLoginEmail({
      emailAddress: state.emailAddress,
      password: state.password,
    })

    if (login.success && login.success.data) {
      if (props.isModal) {
        emit('login-success')
      } else {
        navigateTo(
          loggedUserRedirect({
            userType: login.success.data.userType,
            userId: login.success.data.userId,
          }),
          {
            external: true,
          },
        )
      }
    } else if (login.error) {
      apiResponseError.value = login.error
    }
  }
}

const {
  googleApiResponseError,
  googleLoginIsLoading,
  onErrorGoogleSignIn,
  onSuccessGoogleSignInLogin,
} = useGoogleSignIn(props.isModal)

const { isReady: isGoogleReady, login: loginToGoogle } = useTokenClient({
  onSuccess: onSuccessGoogleSignInLogin,
  onError: onErrorGoogleSignIn,
})

function handleLoginError(error: ApiResponseError) {
  apiResponseError.value = error
  currentState.value = STATES.ERROR
  scrollToTop()
}

watch([apiResponseError, googleApiResponseError], errors => {
  const error = errors.find(e => e)
  if (error) {
    handleLoginError(error)
  }
})

watch([googleLoginIsLoading], ([googleLoading]) => {
  currentState.value = googleLoading ? STATES.LOADING : STATES.IDLE
})
</script>
